import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import Select, { type SelectProps } from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import {
  ALL_CURRENCIES,
  ALL_FINANCE_ACCOUNT_KINDS,
  FinanceAccountId,
  type Currency,
  type FinanceAccount,
  type FinanceAccountKind,
} from '@etiquetachef/types';
import { createFinanceAccountCallable, updateFinanceAccountCallable } from '@/lib/callables';
import { toast } from '@/lib/notifications';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import { formatCentsToInput, parseInputToCents } from '@/lib/money';

type Props = {
  open: boolean;
  mode: 'create' | 'edit';
  account: FinanceAccount | null;
  onClose: () => void;
};

/** Fuso default de conta nova — sede da empresa (EtiquetaChef SRL, Itália). */
const DEFAULT_TIME_ZONE = 'Europe/Rome';

const TIME_ZONE_OPTIONS: SelectProps.Option[] = Intl.supportedValuesOf('timeZone').map((tz) => ({
  value: tz,
  label: tz,
}));

/**
 * Modal de criar/editar uma conta do Financeiro interno (banco/caixa/etc.): nome, tipo, banco,
 * moeda, fuso IANA e saldo de abertura.
 *
 * Origem: #73 — 2026-06-21 19:44 +0200 (Financeiro PR A, cadastros).
 *
 * Por quê: Financeiro INTERNO da empresa (épico #72) — contabilidade da própria EtiquetaChef,
 * não billing de cliente nem multi-tenant. É modal (não rota) porque o admin usa Cloudscape e a
 * regra "formulário vira tela" do CLAUDE.md é do `apps/app`.
 *
 * Contrato: `open`/`mode`/`account` — o formulário é reposto a cada abertura (`useEffect` em
 * `open`). Validação local: nome obrigatório; saldo de abertura via `parseInputToCents`
 * (`lib/money.ts` — aceita `1.800,00` e `1,800.00`; vazio = 0). Submete `createFinanceAccount`
 * / `updateFinanceAccount` (Functions; `requireAdminRole` com `ADMIN_PERMISSIONS.manageFinance`
 * — ROOT/FINANCE, `docs/permissions.md`, #795 ADM-7; OPS/SUPPORT recebem `permission-denied`,
 * que aqui aparece como o erro genérico); `bank` vazio vai como `undefined` no
 * create e `null` no update (apagar o campo). Erro → `Alert` genérico + `reportNonFatal`;
 * sucesso → toast e `onClose()`. Escrita é só via callable — rules de `finance_accounts` têm
 * `allow write: if false` (`match /finance_accounts/{accountId}` em `firebase/firestore.rules`).
 *
 * Usado por: `routes/FinanceAccountsPage.tsx` (web `apps/admin`, rota `/finance/accounts`, menu
 * Financeiro → Contas). Spec: `docs/screens/finance-accounts-admin.md`.
 *
 * Dados: entrada em CENTAVOS (`openingBalanceCents`); doc `finance_accounts/{accountId}`
 * (`FinanceAccountSchema`, `packages/types/src/finance.ts`). `timeZone` default `Europe/Rome`
 * (sede) — é o fuso dos cortes por dia de saldo/caixa (#105).
 *
 * Falhas conhecidas: até 2026-10-13 o `catch` só fazia `console.error` — falha de gravação
 * não chegava ao PostHog (#798 → #935 item 14). Hoje vai como `admin_finance_account_save_failed`.
 *
 * Issues: #72 (épico), #73, #105 (fuso por conta), #795 (ADM-7, capability `manageFinance`).
 *
 * Histórico:
 *  - 2026-07-02 11:27 +0200 — (sem issue, c1e22aafc) campo de fuso horário IANA por conta.
 *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
 */
export function FinanceAccountEditorDialog({
  open,
  mode,
  account,
  onClose,
}: Props): React.JSX.Element | null {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [bank, setBank] = useState('');
  const [kind, setKind] = useState<FinanceAccountKind>('BANK');
  const [currency, setCurrency] = useState<Currency>('BRL');
  const [timeZone, setTimeZone] = useState(DEFAULT_TIME_ZONE);
  const [opening, setOpening] = useState('0.00');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && account) {
      setName(account.name);
      setBank(account.bank ?? '');
      setKind(account.kind);
      setCurrency(account.currency);
      setTimeZone(account.timeZone);
      setOpening(formatCentsToInput(account.openingBalanceCents));
    } else {
      setName('');
      setBank('');
      setKind('BANK');
      setCurrency('BRL');
      setTimeZone(DEFAULT_TIME_ZONE);
      setOpening('0.00');
    }
    setError(null);
  }, [open, mode, account]);

  if (!open) return null;

  const kindOptions: SelectProps.Option[] = ALL_FINANCE_ACCOUNT_KINDS.map((k) => ({
    value: k,
    label: t(`admin.finance.accounts.kind.${k}`),
  }));
  const currencyOptions: SelectProps.Option[] = ALL_CURRENCIES.map((c) => ({
    value: c,
    label: c,
  }));

  /** Nome obrigatório e saldo de abertura em centavos (vazio = 0); `null` bloqueia o envio. */
  function validate(): { openingBalanceCents: number } | null {
    if (name.trim().length === 0) {
      setError(t('admin.finance.accounts.errors.nameRequired'));
      return null;
    }
    const trimmedOpening = opening.trim();
    const cents = trimmedOpening === '' ? 0 : parseInputToCents(trimmedOpening);
    if (cents === null) {
      setError(t('admin.finance.accounts.errors.amountInvalid'));
      return null;
    }
    return { openingBalanceCents: cents };
  }

  /** Cria/atualiza pela callable do modo; `bank` vazio: `undefined` (create), `null` (update). */
  async function submit(): Promise<void> {
    const ok = validate();
    if (!ok) return;
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'create') {
        await createFinanceAccountCallable({
          name: name.trim(),
          bank: bank.trim() === '' ? undefined : bank.trim(),
          kind,
          currency,
          timeZone,
          openingBalanceCents: ok.openingBalanceCents,
        });
        toast.success(t('admin.finance.accounts.success.created'));
      } else if (account) {
        await updateFinanceAccountCallable({
          accountId: FinanceAccountId(account.id),
          name: name.trim(),
          bank: bank.trim() === '' ? null : bank.trim(),
          kind,
          currency,
          timeZone,
          openingBalanceCents: ok.openingBalanceCents,
        });
        toast.success(t('admin.finance.accounts.success.updated'));
      }
      onClose();
    } catch (err) {
      // #935 (item 14, 2026-10-13 11:00 +0200): só `console.*` não chega até nós — o admin
      // não manda DevTools. O report leva a falha ao PostHog (CLAUDE.md, Observabilidade).
      reportNonFatal('admin_finance_account_save_failed', { mode: account ? 'edit' : 'create', ...serializeError(err) });
      setError(t('admin.finance.accounts.errors.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible={open}
      onDismiss={onClose}
      header={t(mode === 'create' ? 'admin.finance.accounts.create' : 'admin.finance.accounts.edit')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              loading={submitting}
              onClick={(): void => {
                void submit();
              }}
            >
              {t('common.save')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {error !== null && <Alert type="error">{error}</Alert>}
        <FormField label={t('admin.finance.accounts.form.name')}>
          <Input
            value={name}
            placeholder={t('admin.finance.accounts.form.namePlaceholder')}
            onChange={({ detail }): void => {
              setName(detail.value);
            }}
          />
        </FormField>
        <FormField label={t('admin.finance.accounts.form.kind')}>
          <Select
            selectedOption={kindOptions.find((o) => o.value === kind) ?? null}
            options={kindOptions}
            onChange={({ detail }): void => {
              setKind(detail.selectedOption.value as FinanceAccountKind);
            }}
          />
        </FormField>
        <FormField label={t('admin.finance.accounts.form.bank')}>
          <Input
            value={bank}
            placeholder={t('admin.finance.accounts.form.bankPlaceholder')}
            onChange={({ detail }): void => {
              setBank(detail.value);
            }}
          />
        </FormField>
        <FormField label={t('admin.finance.accounts.form.currency')}>
          <Select
            selectedOption={currencyOptions.find((o) => o.value === currency) ?? null}
            options={currencyOptions}
            onChange={({ detail }): void => {
              setCurrency(detail.selectedOption.value as Currency);
            }}
          />
        </FormField>
        <FormField
          label={t('admin.finance.accounts.form.timeZone')}
          description={t('admin.finance.accounts.form.timeZoneHint')}
        >
          <Select
            selectedOption={TIME_ZONE_OPTIONS.find((o) => o.value === timeZone) ?? null}
            options={TIME_ZONE_OPTIONS}
            filteringType="auto"
            onChange={({ detail }): void => {
              setTimeZone(detail.selectedOption.value ?? DEFAULT_TIME_ZONE);
            }}
          />
        </FormField>
        <FormField
          label={t('admin.finance.accounts.form.openingBalance')}
          description={t('admin.finance.accounts.form.openingBalanceHint')}
        >
          <Input
            value={opening}
            inputMode="decimal"
            onChange={({ detail }): void => {
              setOpening(detail.value);
            }}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}
